import { RefreshCcw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@shared/components/Button';
import { StatusPill } from '@shared/components/StatusPill';
import { useOnlineStatus } from '@shared/offline/useOnlineStatus';
import { syncEngine } from '@services/offline/syncEngine';
import { useSyncQueue, syncQueueQueryKey } from '@features/sync/hooks/useSyncQueue';
import { queryClient } from '@app/query/queryClient';

type SyncStatusPanelProps = {
  compact?: boolean;
};

export const SyncStatusPanel = ({ compact }: SyncStatusPanelProps) => {
  const { t } = useTranslation();
  const isOnline = useOnlineStatus();
  const { data: queue = [] } = useSyncQueue();
  const pending = queue.filter((item) => item.status === 'pending' || item.status === 'error');
  const syncing = queue.some((item) => item.status === 'syncing');

  const runSync = async () => {
    await syncEngine.processQueue();
    await queryClient.invalidateQueries({ queryKey: syncQueueQueryKey });
  };

  if (compact) {
    return (
      <StatusPill
        status={
          !isOnline ? 'offline' : syncing ? 'syncing' : pending.length > 0 ? 'pending' : 'online'
        }
        dense
      />
    );
  }

  return (
    <div className="flex items-center justify-between gap-4 rounded-lg border border-line bg-white p-4">
      <div>
        <p className="text-sm font-semibold">{t('sync.title')}</p>
        <p className="text-xs text-muted">{t('sync.pendingCount', { count: pending.length })}</p>
      </div>
      <Button type="button" variant="secondary" onClick={() => void runSync()}>
        <RefreshCcw className="h-4 w-4" aria-hidden="true" />
        {t('sync.run')}
      </Button>
    </div>
  );
};
